import React, { useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';

import { useTypedSelector } from 'hooks';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { buildCampaignValues } from '../adapters';
import { MODULE_MAX_WEDGES } from '../adapters/game';
import { WizardPanel } from '../components/WizardFrame';
import ReviewTable from '../kit/ReviewTable';
import { TextField } from '../kit/fields';
import { ErrorList, Words } from '../kit/layout';
import { editAt, IWizardStepProps } from '../wizardModel';
import { PrimaryButton, SecondaryButton } from '../wizardStyles';
import {
  CampaignWizardIntent,
  CampaignWizardResult,
  CampaignWizardSubmit
} from '../wizardSubmission';
import { reviewRows } from './reviewRows';

const TW = `${TOrbit}.wizard`;

// The module's own fallback when the tenant sets no timezone (`pages/campaign/create.tsx`).
const DEFAULT_TIMEZONE = 'Asia/Kuala_Lumpur';

// 6778:21045 `fieldrow`: a 184 label column, then the 460 name input.
const NameRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 18px;

  > :first-child {
    flex: 0 0 198px;
  }
`;

interface IReviewStepProps extends IWizardStepProps {
  onSubmit: CampaignWizardSubmit;
  /** A save that created the campaign. */
  onSaved: (intent: CampaignWizardIntent, result: CampaignWizardResult) => void;
  /** Game only: the mode pills above the table (6778:25970). */
  header?: React.ReactNode;
}

/** The review (6778:20847): name the campaign, check the answers, then save it. */
const ReviewStep: React.FC<IReviewStepProps> = ({
  state,
  update,
  back,
  onSubmit,
  onSaved,
  header
}) => {
  const { t } = useMultiLanguageTranslation();
  const timezone = useTypedSelector(
    store => store.global?.tenantConfig?.localization?.timezone ?? DEFAULT_TIMEZONE
  );
  const [nameMissing, setNameMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const type = state.type ?? 'stamp';
  // The module's wheel stops at 8 wedges, so a larger one is never sent.
  const wheelTooBig =
    type === 'game' &&
    state.game.gameType === 'spin_the_wheel' &&
    state.game.wedges.length > MODULE_MAX_WEDGES;
  const name =
    type === 'game' && state.game.gameType !== null
      ? t(`${TW}.game.${state.game.gameType}`)
      : t(`${TW}.readyName.${type}`);

  // Both buttons lock while a save is in flight, so one click sends one save.
  const submit = async (intent: CampaignWizardIntent) => {
    if (state.name.trim() === '') {
      setNameMissing(true);
      return;
    }
    if (wheelTooBig) {
      setSaveError(t(`${TW}.game.wheel.overModule`));
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const result = await onSubmit({
        intent,
        values: buildCampaignValues(state, { t, timezone, now: () => moment().toISOString() })
      });
      if (result !== null) {
        onSaved(intent, result);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      setSaveError(message !== '' ? message : t(`${TW}.review.saveFailed`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardPanel
      // 6985:5636 and :12976 redraw the Progress and wheel reviews in the 950 panel.
      narrow={type === 'progress' || (type === 'game' && state.game.gameType === 'spin_the_wheel')}
      title={t(`${TW}.ready`, { name })}
      step={null}
      onBack={back}
      question={t(`${TW}.review.question`)}
      testId="orbit-wizard-step-review"
      spreadFooter
      footer={
        <>
          <SecondaryButton type="button" disabled={saving} onClick={() => submit('draft')}>
            {t(`${TW}.saveDraft`)}
          </SecondaryButton>
          <PrimaryButton type="button" disabled={saving} onClick={() => submit('confirm')}>
            {/* 6985:12976 labels the wheel's confirm Finalise; every other review says Confirm. */}
            {t(
              `${TW}.${
                type === 'game' && state.game.gameType === 'spin_the_wheel' ? 'finalise' : 'confirm'
              }`
            )}
          </PrimaryButton>
        </>
      }
    >
      {header}
      <NameRow>
        <Words $muted>{t(`${TW}.review.name`)}</Words>
        <TextField
          label={t(`${TW}.review.name`)}
          placeholder={t(`${TW}.review.namePlaceholder`)}
          width="xl"
          value={state.name}
          onChange={value => {
            setNameMissing(false);
            update(current => ({ ...current, name: value }));
          }}
        />
      </NameRow>
      <ReviewTable
        rows={reviewRows(state, t)}
        onEdit={target => update(current => editAt(current, target))}
      />
      {nameMissing || saveError !== null ? (
        <ErrorList role="alert">
          {nameMissing ? <li>{t(`${TW}.review.nameRequired`)}</li> : null}
          {saveError !== null ? <li>{saveError}</li> : null}
        </ErrorList>
      ) : null}
    </WizardPanel>
  );
};

export default ReviewStep;
